import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { useFavoritesContext } from '../contexts/FavoritesCountContext';
import { useFavorites } from '../utils/useFavorites';
import { useInboxUnread } from '../contexts/InboxUnreadContext';
import {
  conversationListUnreadCount,
  persistLastInboxConversationId,
  readLastInboxConversationId,
} from '../utils/inboxUnreadBadge';
import type {
  ConversationMessage,
  InboxConversationItem,
  MessageAttachment,
  MessagingUserType,
} from '../types/messages';
import MessageAttachmentPicker from '../components/messages/MessageAttachmentPicker';
import {
  messageAttachmentTypeError,
  messageListPreview,
  readMessageAttachment,
} from '../utils/messageAttachment';
import type { StartupProfile, InvestorProfile } from '../types/firestore';
import { useInbox } from '../utils/useInbox';
import { useInboxTyping } from '../utils/useInboxTyping';
import { PRAIRI_SURFACES } from '../constants/prairiSurfaces';
import { resolveStartupId } from '../utils/startupId';
import { startupNameSlug } from '../utils/startupNameSlug';
import { CATALOG_CATEGORY_LABELS } from '../constants/catalog-categories';
import StartupRow from '../components/StartupRow';
import StartupProfileComponent from '../components/profile/StartupProfile';
import InvestorProfileComponent from '../components/profile/InvestorProfile';
import DealTracker from '../components/DealTracker';
import {
  ACTION_LABELS,
  DEAL_STATUS,
  getNextStartupDealStatus,
  USER_TYPES,
  type DealStatus,
} from '../constants/dealTracker';
import { useDealTracking } from '../utils/useDealTracking';
import { inboxRefollowNeedsContacted } from '../utils/inboxRefollowDealStatus';
import profileGrey from '../assets/profile_icon__grey-correct_.png';
import prairiLogo from '../assets/prairiwhite.png';
import profileCardFade from '../assets/profile-card-fade.png';
import messageThreadFade from '../assets/profile-card-fade-flipped.png';
import {
  PRAIRI_TEAM_INVESTOR_CTA_LINE,
  PRAIRI_TEAM_STARTUP_CTA_LINE,
  SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
  buildPrairiTeamFallbackInvestorProfile,
  buildPrairiTeamFallbackProfile,
  buildPrairiTeamWelcomeMessage,
  displayPrairiTeamWelcomeBody,
  displayPrairiTeamWelcomePreview,
  hasStartupWelcomeMessage,
  isLocalPrairiTeamWelcomeConversation,
  hasInvestorWelcomeMessage,
  isNewInvestorAccount,
  isNewStartupAccount,
  isPrairiTeamCatalogStartup,
  isPrairiTeamParticipant,
  inboxConversationToOpen,
  orderInboxConversationsNewestFirst,
  withPrairiTeamWelcomeConversation,
  withoutPrairiTeamConversation,
  type PrairiTeamCatalogMatch,
  type PrairiViewerType,
} from '../constants/prairiTeamWelcomeMessage';
import { canSeePrairiThread } from '../utils/internalTester';
import { loginUrlForInbox } from '../utils/loginNextPath';

function PresenceDot({ className }: { className?: string }) {
  return (
    <span
      className={`inline-block rounded-full ring-1 ring-black/40 bg-emerald-400 ${className || ''}`}
      aria-hidden
    />
  );
}

function conversationDisplayName(user: {
  name?: string | null;
  companyName?: string | null;
  userType?: string | null;
}): string {
  if (isPrairiTeamParticipant(user)) return 'PRAIRI';
  if (user.userType === 'startup') {
    return user.companyName || user.name || 'Startup';
  }
  return user.name || user.companyName || 'Conversation';
}

function parseMessageDate(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatInboxDay(value: string): string {
  const date = parseMessageDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatInboxTime(value: string): string {
  const date = parseMessageDate(value);
  if (!date) return '';
  return `- ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

/** Same minimum as the profile-card pill. */
const CARD_THUMB_MIN_PX = 20;
/** Shared inbox pill — a bit larger than Favorites’ 8% profile thumb. */
const INBOX_THUMB_TRACK_FRACTION = 0.12;

/** One shared short pill size for inbox list, chat, and profile. */
function inboxThumbPxFor(view: number, exact?: number) {
  if (exact && exact > 0) return Math.min(view, exact);
  return Math.min(view, Math.max(CARD_THUMB_MIN_PX, Math.round(view * INBOX_THUMB_TRACK_FRACTION)));
}

/** Same scroll feel as the thread: wheel on the pane, and a short thumb you can drag. */
function InboxScrollColumn({
  className,
  contentKey,
  children,
  thumbOutside = false,
  thumbGutter = false,
  pinToEnd = false,
  scrollToTopKey,
  flowOnMobile = false,
  thumbHeightPx,
  topFadeSrc,
  bottomFade = false,
}: {
  className?: string;
  contentKey?: string | number;
  children: React.ReactNode;
  thumbOutside?: boolean;
  thumbGutter?: boolean;
  pinToEnd?: boolean;
  /** When this changes, the pane opens at the first message instead of a leftover scroll position. */
  scrollToTopKey?: string | number;
  /** Phone: show the full message in the page. Desktop keeps the pane scroll. */
  flowOnMobile?: boolean;
  /** Exact thumb height. Used so the message pill matches the profile card. */
  thumbHeightPx?: number;
  /** Flipped fade under the header. Hidden while the first message is at the top. */
  topFadeSrc?: string;
  /** Fade conversation rows into the black page at the bottom of the inbox list. */
  bottomFade?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const topFadeRef = useRef<HTMLImageElement>(null);
  const thumbHeightRef = useRef(CARD_THUMB_MIN_PX);

  const thumbHeightFor = (view: number) => inboxThumbPxFor(view, thumbHeightPx);

  useEffect(() => {
    const el = scrollRef.current;
    const thumbEl = thumbRef.current;
    if (!el || !thumbEl) return;

    const update = () => {
      const view = el.clientHeight;
      const total = el.scrollHeight;
      if (view <= 0) return;
      const range = total - view;
      const canScroll = range > 1;
      thumbEl.style.opacity = canScroll ? '1' : '0';
      thumbEl.style.pointerEvents = canScroll ? 'auto' : 'none';
      const height = thumbHeightFor(view);
      thumbHeightRef.current = height;
      const maxTop = Math.max(0, view - height);
      const top = range <= 0 ? 0 : (el.scrollTop / range) * maxTop;
      thumbEl.style.top = `${top}px`;
      thumbEl.style.height = `${height}px`;
      if (topFadeRef.current) {
        topFadeRef.current.style.opacity =
          range <= 1 ? '0' : String(Math.min(1, el.scrollTop / 32));
      }
    };

    const pin = () => {
      if (pinToEnd) el.scrollTop = el.scrollHeight;
    };
    update();
    pin();
    update();
    const later = window.setTimeout(() => {
      pin();
      update();
    }, 80);
    const afterLayout = window.setTimeout(() => {
      pin();
      update();
    }, 240);
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      window.clearTimeout(later);
      window.clearTimeout(afterLayout);
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [contentKey, pinToEnd, thumbHeightPx, topFadeSrc]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (pinToEnd) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    if (scrollToTopKey === undefined) return;
    el.scrollTop = 0;
  }, [contentKey, pinToEnd, scrollToTopKey]);

  const onThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    const range = el.scrollHeight - el.clientHeight;
    if (range <= 1) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    const startY = event.clientY;
    const height = thumbHeightRef.current;
    const maxTop = Math.max(0, el.clientHeight - height);
    const startTop = maxTop <= 0 ? 0 : (el.scrollTop / range) * maxTop;

    const onMove = (ev: PointerEvent) => {
      const nextTop = Math.max(0, Math.min(maxTop, startTop + (ev.clientY - startY)));
      const ratio = maxTop <= 0 ? 0 : nextTop / maxTop;
      el.scrollTop = ratio * range;
    };
    const onUp = (ev: PointerEvent) => {
      target.releasePointerCapture(ev.pointerId);
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };

  return (
    <div className={`relative min-h-0 ${className || ''}`}>
      <div
        className={
          thumbOutside
            ? 'absolute inset-0 overflow-hidden rounded-3xl'
            : flowOnMobile
              ? 'max-lg:relative lg:absolute lg:inset-0'
              : 'absolute inset-0'
        }
      >
        <div
          ref={scrollRef}
          className={
            flowOnMobile
              ? `max-lg:relative max-lg:overflow-visible lg:absolute lg:inset-y-0 lg:left-0 lg:overflow-y-auto lg:inbox-native-hidden ${
                  thumbGutter ? 'lg:right-4' : 'lg:right-0'
                }`
              : `absolute inset-y-0 left-0 overflow-y-auto inbox-native-hidden ${
                  thumbGutter ? 'right-4' : 'right-0'
                }`
          }
        >
          {children}
        </div>
      </div>
      <div
        className={`pointer-events-none absolute inset-y-0 z-30 w-3 ${
          flowOnMobile ? 'max-lg:hidden' : ''
        } ${thumbOutside ? 'left-full' : 'right-0'}`}
      >
        <div
          ref={thumbRef}
          role="scrollbar"
          aria-orientation="vertical"
          className="pointer-events-auto absolute right-0 w-3 cursor-grab touch-none active:cursor-grabbing"
          style={{ top: 0, height: CARD_THUMB_MIN_PX }}
          onPointerDown={onThumbPointerDown}
        >
          <div
            className={`pointer-events-none absolute inset-y-0 w-1 rounded-full bg-[#8a8a8a] ${
              thumbOutside ? 'left-1' : 'right-1'
            }`}
          />
        </div>
      </div>
      {topFadeSrc ? (
        <img
          ref={topFadeRef}
          src={topFadeSrc}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-20 h-20 w-full object-fill lg:h-[189px]"
          style={{ opacity: 0 }}
        />
      ) : null}
      {bottomFade ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-24 lg:h-32"
          style={{
            background: `linear-gradient(to top, ${PRAIRI_SURFACES.divider} 0%, transparent 100%)`,
          }}
        />
      ) : null}
    </div>
  );
}

/** Darker than the near-white date rule. Same grey as the deal-tracker line. */
const INBOX_DATE_GREY = '#696969';

/** Same 16:9 crop as startup thumbnail images. */
const STARTUP_THUMB = {
  header: 'w-24 aspect-video rounded-sm lg:w-[calc(5rem*16/9)]',
  message: 'w-[4.5rem] aspect-video rounded-sm lg:h-14 lg:w-auto',
  list: 'w-20 aspect-video rounded-md lg:w-[7.5rem]',
  receipt: 'h-4 aspect-video rounded-sm',
} as const;

/** Startup thread photo, or the PRAIRI mark on the same fill as its column. */
function ConversationAvatar({
  user,
  size,
  surfaceClass,
}: {
  user: {
    name?: string | null;
    companyName?: string | null;
    userType: MessagingUserType;
    profilePictureUrl?: string | null;
  };
  size: 'list' | 'header' | 'message' | 'receipt';
  surfaceClass?: string;
}) {
  const isStartup = user.userType === 'startup';
  const isPrairiTeam = isPrairiTeamParticipant(user);
  const startupThumb = STARTUP_THUMB[size];
  const photoUrl = user.profilePictureUrl?.trim() || '';
  const circleClass =
    size === 'header'
      ? 'h-14 w-14 lg:h-16 lg:w-16'
      : size === 'message'
        ? 'h-12 w-12'
        : size === 'receipt'
          ? 'h-5 w-5'
          : 'h-12 w-12 lg:h-16 lg:w-16';

  if (isPrairiTeam) {
    const imgClass =
      size === 'header'
        ? 'h-10'
        : size === 'message'
          ? 'h-7'
          : size === 'receipt'
            ? 'h-2.5'
            : 'h-5';
    const wordClass =
      size === 'header' ? 'text-[13px]' : size === 'message' ? 'text-[9px]' : 'text-[10px]';
    return (
      <div
        className={`relative ${startupThumb} ${surfaceClass || 'bg-[#1d1d1d]'} border border-[#444444] flex-shrink-0 overflow-hidden`}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          <img
            src={prairiLogo}
            alt=""
            className={`${imgClass} w-auto object-contain mix-blend-lighten`}
          />
          <span className={`${wordClass} text-white tracking-[0.28em] font-semibold leading-none`}>
            PRAIRI
          </span>
        </div>
      </div>
    );
  }

  if (!photoUrl) {
    const frame = isStartup ? startupThumb : `${circleClass} rounded-full`;
    return (
      <span
        className={`flex ${frame} flex-shrink-0 items-center justify-center overflow-hidden border border-[#444444] ${surfaceClass || 'bg-[#1d1d1d]'}`}
      >
        <img src={profileGrey} alt="" className="h-[84%] w-[84%] translate-y-[8%] object-contain" />
      </span>
    );
  }

  return (
    <img
      src={photoUrl}
      alt=""
      className={`object-cover flex-shrink-0 border border-[#444444] ${
        isStartup ? startupThumb : `${circleClass} rounded-full`
      }`}
    />
  );
}

function MessageBody({
  body,
  attachment,
}: {
  body: string;
  attachment?: MessageAttachment | null;
}) {
  if (!body && !attachment?.url) return null;
  const cta = [
    { line: PRAIRI_TEAM_STARTUP_CTA_LINE, to: '/profile/startup' },
    { line: PRAIRI_TEAM_INVESTOR_CTA_LINE, to: '/dashboard' },
  ].find(item => body.includes(item.line));
  const ctaAt = cta ? body.indexOf(cta.line) : -1;
  return (
    <>
      {body && cta && ctaAt >= 0 ? (
        <p className="whitespace-pre-wrap break-words text-left">
          {body.slice(0, ctaAt)}
          <Link to={cta.to} className="underline underline-offset-2 hover:text-electric-400">
            {cta.line}
          </Link>
          {body.slice(ctaAt + cta.line.length)}
        </p>
      ) : body ? (
        <p className="whitespace-pre-wrap break-words text-left">{body}</p>
      ) : null}
      {attachment?.url ? (
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex max-w-full text-sm underline underline-offset-2"
        >
          {attachment.name || 'Attachment'}
        </a>
      ) : null}
    </>
  );
}

const InboxPage: React.FC = () => {
  const { user, firebaseUser, loading: authLoading, userLoading } = useAuth();
  const { startupProfile, investorProfile, reloadProfile } = useProfile();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const fromConversationId = searchParams.get('conversationId');
  const appliedDeepLinkRef = useRef<string | null>(null);
  const userPickedConversationRef = useRef(false);

  const {
    conversations,
    prairiTeamOnline,
    loadingConversations,
    conversationError,
    refetchConversations,
    updateConversation,
    getMessages,
    markAsRead,
    sendReply,
  } = useInbox();
  const {
    favorites,
    favoritesReady,
    invalidateFavorites,
    removeFavoriteLocally,
    addFavoriteLocally,
  } = useFavoritesContext();
  const { addToFavorites, isLoading: readdingFavorite } = useFavorites();
  const {
    welcomeViewed,
    markPrairi31WelcomeViewed,
    markConversationViewed,
    refreshInboxUnread,
    setOpenInboxConversation,
  } = useInboxUnread();

  // Opening Messages clears the sticky PRAIRI welcome "1" even before a thread is selected.
  useEffect(() => {
    if (user?.userType === 'investor' && !welcomeViewed) {
      markPrairi31WelcomeViewed();
    }
  }, [user?.userType, welcomeViewed, markPrairi31WelcomeViewed]);

  const inboxConversationsRef = useRef<InboxConversationItem[]>([]);
  const favoriteIds = useMemo(
    () => new Set(favorites.map(item => resolveStartupId(item)).filter(Boolean) as string[]),
    [favorites]
  );

  const handleInboxDismissFromFeed = useCallback((info: { startupId: string }) => {
    const startupId = info.startupId;
    if (!startupId || startupId === SYNTHETIC_PRAIRI_TEAM_STARTUP_ID) return;
    const dismissFromFeed = httpsCallable<{ startupId: string }, { success: boolean }>(
      getFunctions(),
      'dismissFromFeed'
    );
    void dismissFromFeed({ startupId }).catch(err => {
      console.error('Error dismissing from feed:', err);
    });
  }, []);

  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  /** Phone shows the list until a thread is opened. Desktop keeps both panes. */
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const profileCardRef = useRef<HTMLElement | null>(null);
  /** Same pixel height for list / chat / profile scroll pills. */
  const [inboxThumbPx, setInboxThumbPx] = useState(0);

  useEffect(() => {
    const el = profileCardRef.current;
    if (!el) {
      setInboxThumbPx(0);
      return;
    }
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h <= 0) return;
      const next = Math.max(CARD_THUMB_MIN_PX, Math.round(h * INBOX_THUMB_TRACK_FRACTION));
      setInboxThumbPx(prev => (prev === next ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedConversationId]);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [messagesReloadToken, setMessagesReloadToken] = useState(0);
  const [composerText, setComposerText] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const inboxLoadedRef = useRef(false);
  const prevLoadingConversationsRef = useRef(loadingConversations);
  if (prevLoadingConversationsRef.current && !loadingConversations) {
    inboxLoadedRef.current = true;
  }
  prevLoadingConversationsRef.current = loadingConversations;
  /** Startup inbox: confirmed inactive favorite for the open thread (belt-and-suspenders). */
  const [startupThreadEnded, setStartupThreadEnded] = useState(false);
  const [selectedStartupProfile, setSelectedStartupProfile] = useState<StartupProfile | null>(null);
  const [selectedInvestorProfile, setSelectedInvestorProfile] = useState<InvestorProfile | null>(
    null
  );
  const startupProfileCache = useRef<Map<string, StartupProfile>>(new Map());
  const investorProfileCache = useRef<Map<string, InvestorProfile>>(new Map());
  const [prairiTeamStartup, setPrairiTeamStartup] = useState<PrairiTeamCatalogMatch | null>(null);

  useEffect(() => {
    if (user?.userType !== 'investor' || !canSeePrairiThread(user)) {
      setPrairiTeamStartup(null);
      return;
    }
    let cancelled = false;
    const getApprovedStartups = httpsCallable<
      Record<string, never>,
      {
        startups?: Array<{
          id?: string;
          userId?: string;
          companyName?: string;
          slogan?: string;
          thumbnailUrl?: string;
        }>;
      }
    >(getFunctions(), 'getApprovedStartups');
    getApprovedStartups({})
      .then(result => {
        if (cancelled) return;
        const team = (result.data.startups || []).find(item => isPrairiTeamCatalogStartup(item));
        if (!team) return;
        const id = String(team.userId || team.id || '');
        if (!id) return;
        setPrairiTeamStartup({
          id,
          companyName: team.companyName,
          profilePictureUrl: team.thumbnailUrl,
        });
      })
      .catch(() => {
        // Welcome thread still shows without the catalog thumbnail.
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const showPrairiThread =
    (user?.userType === 'investor' || user?.userType === 'startup') && canSeePrairiThread(user);
  const prairiViewer: PrairiViewerType = user?.userType === 'startup' ? 'startup' : 'investor';
  const newAccountWelcome =
    user?.userType === 'startup'
      ? isNewStartupAccount(user?.createdAt)
      : isNewInvestorAccount(user?.createdAt);

  const inboxConversations = useMemo((): InboxConversationItem[] => {
    if (!showPrairiThread) {
      return orderInboxConversationsNewestFirst(withoutPrairiTeamConversation(conversations));
    }
    return withPrairiTeamWelcomeConversation(conversations, {
      investorId: user?.id || firebaseUser?.uid,
      team: prairiTeamStartup,
      prairiTeamOnline,
      viewer: prairiViewer,
      newAccount: newAccountWelcome,
    });
  }, [
    conversations,
    firebaseUser?.uid,
    newAccountWelcome,
    prairiTeamOnline,
    prairiTeamStartup,
    prairiViewer,
    showPrairiThread,
    user?.id,
  ]);

  useEffect(() => {
    if (!inboxConversations.length) return;

    const viewerId = user?.id || firebaseUser?.uid;

    // Deep link from email / Contact: apply once, then stop forcing so clicks can switch threads.
    if (
      fromConversationId &&
      appliedDeepLinkRef.current !== fromConversationId &&
      inboxConversations.some(item => item.conversationId === fromConversationId)
    ) {
      appliedDeepLinkRef.current = fromConversationId;
      userPickedConversationRef.current = true;
      setSelectedConversationId(fromConversationId);
      setMobileThreadOpen(true);
      persistLastInboxConversationId(viewerId, fromConversationId);
      const next = new URLSearchParams(searchParams);
      next.delete('conversationId');
      setSearchParams(next, { replace: true });
      return;
    }

    // Wait for the deep-linked thread to appear before falling back.
    if (
      fromConversationId &&
      appliedDeepLinkRef.current !== fromConversationId &&
      !loadingConversations &&
      inboxLoadedRef.current
    ) {
      appliedDeepLinkRef.current = fromConversationId;
      const next = new URLSearchParams(searchParams);
      next.delete('conversationId');
      setSearchParams(next, { replace: true });
    } else if (
      fromConversationId &&
      appliedDeepLinkRef.current !== fromConversationId &&
      (loadingConversations || !inboxLoadedRef.current)
    ) {
      return;
    }

    // The welcome thread is injected before the inbox loads. Wait for that fetch
    // so we open the thread they last looked at, not PRAIRI or the newest chat.
    if (!inboxLoadedRef.current || loadingConversations) return;

    const stillOpen =
      selectedConversationId &&
      inboxConversations.some(item => item.conversationId === selectedConversationId);
    if (userPickedConversationRef.current && stillOpen) return;

    const nextId = inboxConversationToOpen(
      inboxConversations,
      readLastInboxConversationId(viewerId)
    );
    if (nextId && nextId !== selectedConversationId) {
      setSelectedConversationId(nextId);
    }
  }, [
    inboxConversations,
    fromConversationId,
    loadingConversations,
    searchParams,
    selectedConversationId,
    setSearchParams,
    user?.id,
    firebaseUser?.uid,
  ]);

  useEffect(() => {
    if (!inboxLoadedRef.current || loadingConversations || !selectedConversationId) return;
    persistLastInboxConversationId(user?.id || firebaseUser?.uid, selectedConversationId);
  }, [selectedConversationId, loadingConversations, user?.id, firebaseUser?.uid]);

  useEffect(() => {
    setComposerText('');
    setAttachmentFile(null);
    setAttachmentError(null);
    setMessageError(null);
  }, [selectedConversationId]);

  const selectedConversation: InboxConversationItem | null = useMemo(
    () => inboxConversations.find(item => item.conversationId === selectedConversationId) ?? null,
    [inboxConversations, selectedConversationId]
  );
  const {
    dealStatus: inboxDealStatus,
    isLoading: inboxDealLoading,
    isUpdating: inboxDealUpdating,
    updateDealStatus: updateInboxDealStatus,
  } = useDealTracking({
    investorId:
      user?.userType === 'startup' &&
      selectedConversation?.investorId &&
      !isPrairiTeamParticipant(selectedConversation.otherUser)
        ? selectedConversation.investorId
        : undefined,
  });
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const startupInboxDealStatus: DealStatus =
    inboxDealStatus === DEAL_STATUS.notContacted ? DEAL_STATUS.favorited : inboxDealStatus;

  const advanceStartupInboxDeal = async () => {
    if (startupInboxDealStatus === DEAL_STATUS.favorited) {
      composerRef.current?.focus();
      return;
    }
    const nextStatus = getNextStartupDealStatus(startupInboxDealStatus);
    if (!nextStatus || inboxDealUpdating) return;
    try {
      await updateInboxDealStatus(nextStatus);
    } catch {
      // Leave the tracker on the current step when the update fails.
    }
  };

  const rewindStartupInboxDeal = async (step: DealStatus) => {
    if (inboxDealUpdating || step === startupInboxDealStatus) return;
    try {
      await updateInboxDealStatus(step);
    } catch {
      // Leave the tracker on the current step when the update fails.
    }
  };

  // Startup: confirm whether this investor still has an active favorite (drives the red notice).
  useEffect(() => {
    if (
      user?.userType !== 'startup' ||
      !selectedConversation?.investorId ||
      isPrairiTeamParticipant(selectedConversation.otherUser)
    ) {
      setStartupThreadEnded(false);
      return;
    }

    let cancelled = false;
    const conversationId = selectedConversation.conversationId;
    const investorId = selectedConversation.investorId;
    const getStatusFn = httpsCallable(getFunctions(), 'getDealStatus');

    getStatusFn({ investorId })
      .then(result => {
        if (cancelled) return;
        const data = result.data as {
          success?: boolean;
          favoriteActive?: boolean;
        };
        const ended = data?.success === true && data.favoriteActive === false;
        setStartupThreadEnded(ended);
        if (data?.success) {
          // Do not set isActive false — that made the thread look dropped; keep history listed.
          // Favoriting again must clear the old "not pursuing" state.
          updateConversation(conversationId, { favoriteActive: data.favoriteActive === true });
        }
      })
      .catch(() => {
        if (!cancelled) setStartupThreadEnded(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    user?.userType,
    selectedConversation?.conversationId,
    selectedConversation?.investorId,
    selectedConversation?.otherUser,
    updateConversation,
  ]);

  inboxConversationsRef.current = inboxConversations;

  useEffect(() => {
    setOpenInboxConversation(selectedConversationId);
    return () => setOpenInboxConversation(null);
  }, [selectedConversationId, setOpenInboxConversation]);

  useEffect(() => {
    if (!selectedConversationId) return;
    const opened = inboxConversationsRef.current.find(
      item => item.conversationId === selectedConversationId
    );
    if (!opened) return;

    if (isPrairiTeamParticipant(opened.otherUser)) {
      markPrairi31WelcomeViewed();
    }
    if (isLocalPrairiTeamWelcomeConversation(selectedConversationId)) {
      return;
    }

    const openedId = selectedConversationId;
    updateConversation(openedId, { unreadCount: 0 });
    markConversationViewed(openedId, opened.lastMessageAt ?? null);

    let cancelled = false;
    void markAsRead(openedId)
      .then(() => {
        if (!cancelled) return refreshInboxUnread();
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [
    selectedConversationId,
    markAsRead,
    markConversationViewed,
    markPrairi31WelcomeViewed,
    refreshInboxUnread,
    updateConversation,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadMessages = async () => {
      if (!selectedConversationId) {
        setMessages([]);
        setMessageError(null);
        return;
      }

      if (isLocalPrairiTeamWelcomeConversation(selectedConversationId)) {
        if (cancelled) return;
        setMessageError(null);
        setMessages(
          user?.id
            ? [
                user.userType === 'startup'
                  ? buildPrairiTeamWelcomeMessage({
                      investorId: SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
                      startupId: user.id,
                      viewer: 'startup',
                      newAccount: isNewStartupAccount(user.createdAt),
                    })
                  : buildPrairiTeamWelcomeMessage({
                      investorId: user.id,
                      startupId:
                        selectedConversation?.startupId || SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
                      newAccount: isNewInvestorAccount(user.createdAt),
                    }),
              ]
            : []
        );
        setLoadingMessages(false);
        return;
      }

      setLoadingMessages(true);
      setMessageError(null);
      const response = await getMessages({ conversationId: selectedConversationId, limit: 50 });
      if (cancelled) return;
      if (!response) {
        setMessages([]);
        setMessageError('Could not load messages');
        setLoadingMessages(false);
        return;
      }
      if (!response.success) {
        setMessages([]);
        setMessageError(response.error || 'Could not load messages');
        setLoadingMessages(false);
        return;
      }
      const isStartupViewer = user?.userType === 'startup';
      const hasWelcome = isStartupViewer
        ? hasStartupWelcomeMessage(response.messages.map(message => message.body))
        : hasInvestorWelcomeMessage(response.messages.map(message => message.body));
      const welcome =
        user?.id &&
        selectedConversation &&
        isPrairiTeamParticipant(selectedConversation.otherUser) &&
        !hasWelcome
          ? [
              isStartupViewer
                ? buildPrairiTeamWelcomeMessage({
                    investorId: selectedConversation.investorId,
                    startupId: user.id,
                    viewer: 'startup',
                  })
                : buildPrairiTeamWelcomeMessage({
                    investorId: user.id,
                    startupId: selectedConversation.startupId,
                  }),
            ]
          : [];
      setMessages([...welcome, ...response.messages]);
      setLoadingMessages(false);
    };

    void loadMessages();
    return () => {
      cancelled = true;
    };
  }, [
    selectedConversationId,
    selectedConversation?.startupId,
    user?.id,
    user?.userType,
    getMessages,
    messagesReloadToken,
  ]);

  useEffect(() => {
    if (user?.userType !== 'investor' && user?.userType !== 'startup') return;
    const refreshList = () => {
      if (document.visibilityState === 'hidden') return;
      void refetchConversations({ silent: true });
    };
    const intervalId = window.setInterval(refreshList, 10000);
    window.addEventListener('focus', refreshList);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshList);
    };
  }, [refetchConversations, user?.userType]);

  useEffect(() => {
    if (!selectedConversationId || isLocalPrairiTeamWelcomeConversation(selectedConversationId)) {
      return;
    }
    const openId = selectedConversationId;
    let cancelled = false;
    const refreshOpenThread = () => {
      if (document.visibilityState === 'hidden' || cancelled) return;
      void getMessages({ conversationId: openId, limit: 50 }).then(response => {
        if (cancelled || !response?.success) return;
        let arrived = false;
        setMessages(prev => {
          const serverIds = new Set(response.messages.map(message => message.id));
          const pending = prev.filter(
            message => message.id.startsWith('optimistic-') && !serverIds.has(message.id)
          );
          const welcome = prev.filter(message => message.id.endsWith('-message'));
          const merged = [...welcome, ...response.messages, ...pending];
          if (
            merged.length === prev.length &&
            merged.every((message, index) => message.id === prev[index]?.id)
          ) {
            return prev;
          }
          arrived = true;
          return merged;
        });
        if (!arrived || cancelled) return;
        const newest = response.messages[response.messages.length - 1];
        updateConversation(openId, { unreadCount: 0 });
        markConversationViewed(openId, newest?.createdAt ?? null);
        void markAsRead(openId)
          .then(() => refreshInboxUnread())
          .catch(() => undefined);
      });
    };
    const intervalId = window.setInterval(refreshOpenThread, 10000);
    window.addEventListener('focus', refreshOpenThread);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshOpenThread);
    };
  }, [
    getMessages,
    markAsRead,
    markConversationViewed,
    refreshInboxUnread,
    selectedConversationId,
    updateConversation,
  ]);

  useEffect(() => {
    if (!selectedConversation || user?.userType !== 'investor') {
      setSelectedStartupProfile(null);
      return;
    }
    const isPrairiTeamThread = isPrairiTeamParticipant(selectedConversation.otherUser);
    const cached = startupProfileCache.current.get(selectedConversation.startupId);
    if (cached) {
      setSelectedStartupProfile(cached);
      return;
    }
    setSelectedStartupProfile(isPrairiTeamThread ? buildPrairiTeamFallbackProfile() : null);
    if (selectedConversation.startupId === SYNTHETIC_PRAIRI_TEAM_STARTUP_ID) {
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      const fns = getFunctions();
      const getProfile = httpsCallable<
        { userId: string },
        { success: boolean; profile?: StartupProfile }
      >(fns, 'getStartupProfile');
      getProfile({ userId: selectedConversation.startupId })
        .then(result => {
          if (cancelled) return;
          if (result.data.success && result.data.profile) {
            startupProfileCache.current.set(selectedConversation.startupId, result.data.profile);
            setSelectedStartupProfile(result.data.profile);
          }
        })
        .catch(() => {
          // best effort
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selectedConversation?.startupId, user?.userType]);

  useEffect(() => {
    const otherIsInvestor = selectedConversation?.otherUser.userType === 'investor';
    if (!selectedConversation || (user?.userType !== 'startup' && !otherIsInvestor)) {
      setSelectedInvestorProfile(null);
      return;
    }
    if (selectedConversation.investorId === SYNTHETIC_PRAIRI_TEAM_STARTUP_ID) {
      // The new-startup welcome is not tied to an investor account.
      setSelectedInvestorProfile(buildPrairiTeamFallbackInvestorProfile());
      return;
    }
    const cached = investorProfileCache.current.get(selectedConversation.investorId);
    if (cached) {
      setSelectedInvestorProfile(cached);
      return;
    }
    setSelectedInvestorProfile(null);
    let cancelled = false;
    const timer = setTimeout(() => {
      const fns = getFunctions();
      const getProfile = httpsCallable<
        { userId: string },
        { success: boolean; profile?: InvestorProfile }
      >(fns, 'getInvestorProfile');
      getProfile({ userId: selectedConversation.investorId })
        .then(result => {
          if (cancelled) return;
          if (result.data.success && result.data.profile) {
            investorProfileCache.current.set(selectedConversation.investorId, result.data.profile);
            setSelectedInvestorProfile(result.data.profile);
          }
        })
        .catch(() => {
          // best effort
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selectedConversation?.investorId, selectedConversation?.otherUser.userType, user?.userType]);

  const selectedConversationEnded = useMemo(() => {
    if (!selectedConversation || isPrairiTeamParticipant(selectedConversation.otherUser)) {
      return false;
    }
    if (startupThreadEnded) return true;
    // Investor: local favorites list is source of truth once ready (covers unfollow
    // and optimistic re-follow via addFavoriteLocally / removeFavoriteLocally).
    if (user?.userType === 'investor' && favoritesReady && selectedConversation.startupId) {
      return !favoriteIds.has(selectedConversation.startupId);
    }
    // Backend flag after unfavorite (favorite soft-delete)
    if (selectedConversation.favoriteActive === false) return true;
    // Re-adding sets this true before the favorites list refreshes.
    if (selectedConversation.favoriteActive === true) return false;
    return false;
  }, [selectedConversation, user?.userType, favoritesReady, favoriteIds, startupThreadEnded]);

  const typingIds = useInboxTyping({
    enabled: user?.userType === 'investor' || user?.userType === 'startup',
    conversationId:
      selectedConversationEnded ||
      isLocalPrairiTeamWelcomeConversation(selectedConversationId || '') ||
      isPrairiTeamParticipant(selectedConversation?.otherUser || {})
        ? null
        : selectedConversationId,
    composerText,
    sending,
  });

  if (!firebaseUser) {
    const search = searchParams.toString();
    return <Navigate to={loginUrlForInbox('/inbox', search ? `?${search}` : '')} replace />;
  }

  if (authLoading || userLoading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user?.userType !== 'investor' && user?.userType !== 'startup') {
    return <Navigate to="/dashboard" />;
  }

  const isInvestor = user.userType === 'investor';
  const isStartup = user.userType === 'startup';
  const showStartupProfilePanel =
    isInvestor &&
    Boolean(selectedConversation) &&
    selectedConversation?.otherUser.userType !== 'investor';
  const showInvestorProfilePanel =
    isStartup || selectedConversation?.otherUser.userType === 'investor';
  // 2-column outer grid: left sidebar (28%) and main container. Desktop sections share one bottom inset.
  const inboxGridCols = 'lg:grid-cols-[28%_minmax(0,1fr)]';
  const inboxSectionBottom = 'lg:mb-3 lg:h-[calc(100%-0.75rem)] lg:max-h-[calc(100%-0.75rem)]';

  const handleSend = async () => {
    if (
      !selectedConversation ||
      (!composerText.trim() && !attachmentFile) ||
      sending ||
      selectedConversationEnded
    )
      return;

    setSending(true);
    setMessageError(null);

    const text = composerText;
    const file = attachmentFile;
    let attachmentPayload = null;
    if (file) {
      try {
        attachmentPayload = await readMessageAttachment(file);
      } catch (error) {
        setAttachmentError(error instanceof Error ? error.message : 'Could not read that file.');
        setSending(false);
        return;
      }
    }
    const now = new Date().toISOString();
    const tempId = `optimistic-${Date.now()}`;
    const optimisticAttachment: MessageAttachment | null = file
      ? {
          name: file.name,
          contentType: file.type,
          url: URL.createObjectURL(file),
          size: file.size,
        }
      : null;

    // Update UI immediately — before the network call
    setMessages(prev => [
      ...prev,
      {
        id: tempId,
        senderId: user.id,
        recipientId: selectedConversation.otherUser.id,
        senderType: user.userType as MessagingUserType,
        body: text,
        createdAt: now,
        readAt: null,
        delivery: null,
        attachment: optimisticAttachment,
      },
    ]);
    setComposerText('');
    setAttachmentFile(null);
    setAttachmentError(null);
    updateConversation(selectedConversation.conversationId, {
      lastMessagePreview: messageListPreview(text, file?.name),
      lastMessageAt: now,
      lastSenderId: user.id,
      updatedAt: now,
    });

    const recipientId = isLocalPrairiTeamWelcomeConversation(selectedConversation.conversationId)
      ? SYNTHETIC_PRAIRI_TEAM_STARTUP_ID
      : selectedConversation.otherUser.id;

    const result = await sendReply({
      recipientId,
      body: text,
      attachment: attachmentPayload,
    });

    if (optimisticAttachment?.url.startsWith('blob:')) {
      URL.revokeObjectURL(optimisticAttachment.url);
    }

    if (!result.success) {
      // Rollback optimistic message and restore composer
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setComposerText(text);
      setAttachmentFile(file);
      setMessageError(result.error || 'Could not send message');
      setSending(false);
      return;
    }

    // Swap temp ID for the real server-assigned ID and timestamp
    setMessages(prev =>
      prev.map(m =>
        m.id === tempId
          ? {
              ...m,
              id: result.messageId,
              createdAt: result.createdAt,
              attachment: result.attachment ?? m.attachment,
            }
          : m
      )
    );
    await refetchConversations({ silent: true });
    if (result.conversationId !== selectedConversation.conversationId) {
      userPickedConversationRef.current = true;
      setSelectedConversationId(result.conversationId);
      persistLastInboxConversationId(user?.id || firebaseUser?.uid, result.conversationId);
    }
    void refreshInboxUnread();

    setSending(false);
  };

  const handleOwnPhotoSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user?.id || user.userType !== 'investor') return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) return;
    if (file.size > 5 * 1024 * 1024) return;

    setPhotoUploading(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const upload = httpsCallable<
        { userId: string; file: { name: string; contentType: string; data: string } },
        { success?: boolean }
      >(getFunctions(), 'uploadInvestorPicture');
      const result = await upload({
        userId: user.id,
        file: { name: file.name, contentType: file.type, data: base64 },
      });
      if (result.data?.success) await reloadProfile();
    } catch (err) {
      console.error('Error uploading profile photo from chat:', err);
    } finally {
      setPhotoUploading(false);
    }
  };

  return (
    <div className="bg-black">
      <div className="pl-2 lg:pl-3 pr-0 lg:pr-0 lg:flex lg:h-[calc(100dvh-3.25rem)] lg:flex-col lg:overflow-hidden lg:pb-0">
        <div
          className={`grid grid-cols-1 gap-y-4 lg:gap-x-2 h-full lg:h-auto lg:min-h-0 lg:flex-1 ${inboxGridCols}`}
        >
          <aside
            className={`relative bg-black min-h-[calc(100dvh-4.5rem)] flex-col overflow-hidden px-0 lg:flex lg:min-h-0 ${inboxSectionBottom} ${
              mobileThreadOpen ? 'hidden' : 'flex'
            }`}
          >
            <div className="pointer-events-none relative z-20 flex justify-center -mb-4 flex-shrink-0">
              <div
                className="w-fit min-w-[9.5rem] px-12 py-2 rounded-b-2xl border-x border-b border-gray-800 bg-[#2e5161] text-white text-sm !font-normal leading-snug text-center shadow-[0_8px_20px_rgba(0,0,0,0.22)]"
                style={{ fontFamily: 'Rubik, sans-serif', fontWeight: 400 }}
              >
                Inbox
              </div>
            </div>
            <InboxScrollColumn
              className="flex-1"
              contentKey={`${inboxConversations.length}:${selectedConversationId || ''}`}
              thumbHeightPx={inboxThumbPx > 0 ? inboxThumbPx : undefined}
              bottomFade
            >
              {loadingConversations ? (
                <div className="h-full flex items-center justify-center text-gray-400">
                  Loading conversations...
                </div>
              ) : conversationError ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-3 px-3">
                  <p className="text-red-400 text-sm">{conversationError}</p>
                  <button
                    onClick={() => refetchConversations()}
                    className="px-3 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm"
                  >
                    Retry
                  </button>
                </div>
              ) : inboxConversations.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  No conversations yet.
                </div>
              ) : (
                <div className="space-y-1.5 pb-20 lg:pb-28">
                  {inboxConversations.map(item => {
                    const selected = item.conversationId === selectedConversationId;
                    const isPrairiTeam = isPrairiTeamParticipant(item.otherUser);
                    const listUnread = conversationListUnreadCount(item, {
                      isInvestor: user?.userType === 'investor',
                      welcomeViewed,
                      showPrairiWelcome: showPrairiThread,
                    });
                    const unread = listUnread > 0 && !selected;
                    const dayLabel = item.lastMessageAt ? formatInboxDay(item.lastMessageAt) : '';
                    const rowBg = unread
                      ? 'bg-[#2e5161]'
                      : selected
                        ? 'bg-[#333333]'
                        : 'bg-[#1d1d1d]';
                    const isItemEnded =
                      !isPrairiTeam &&
                      item.favoriteActive !== true &&
                      (item.favoriteActive === false ||
                        (user?.userType === 'investor' &&
                          favoritesReady &&
                          item.startupId &&
                          !favoriteIds.has(item.startupId)));
                    return (
                      <button
                        key={item.conversationId}
                        onClick={() => {
                          userPickedConversationRef.current = true;
                          setSelectedConversationId(item.conversationId);
                          setMobileThreadOpen(true);
                          persistLastInboxConversationId(
                            user?.id || firebaseUser?.uid,
                            item.conversationId
                          );
                        }}
                        className={`group relative w-full min-h-[4.75rem] text-left pl-2.5 pr-3 py-3 border-l-[3px] transition-colors lg:min-h-[7.5rem] lg:py-4.5 ${rowBg} ${
                          selected
                            ? 'border-l-emerald-400'
                            : unread
                              ? 'border-l-transparent'
                              : 'border-l-transparent hover:bg-[#262626]'
                        }`}
                      >
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className="relative flex-shrink-0">
                            <ConversationAvatar
                              user={item.otherUser}
                              size="list"
                              surfaceClass={
                                selected || unread ? rowBg : `${rowBg} group-hover:bg-[#262626]`
                              }
                            />
                            {isItemEnded ? (
                              <span
                                className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-1 ring-black/40 bg-red-500"
                                title="Conversation ended"
                              />
                            ) : item.otherUser.isOnline === true ? (
                              <PresenceDot className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5" />
                            ) : null}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p
                                className={`min-w-0 flex-1 text-white text-base font-bold truncate ${
                                  isPrairiTeam ? 'tracking-[0.32em]' : ''
                                }`}
                              >
                                {conversationDisplayName(item.otherUser)}
                              </p>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {unread && (
                                  <span className="inline-flex h-5 min-w-5 px-1.5 items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
                                    {listUnread}
                                  </span>
                                )}
                                {dayLabel && (
                                  <span
                                    className="whitespace-nowrap text-xs"
                                    style={{ color: INBOX_DATE_GREY }}
                                  >
                                    {dayLabel}
                                  </span>
                                )}
                              </div>
                            </div>
                            <p
                              className={`text-sm mt-1 line-clamp-1 lg:line-clamp-2 ${
                                isStartup && isItemEnded
                                  ? 'text-red-500'
                                  : typingIds.has(item.conversationId) || item.otherUserTyping
                                    ? 'italic'
                                    : `text-gray-300 ${isPrairiTeam ? 'whitespace-pre-line' : ''}`
                              }`}
                              style={
                                !isItemEnded &&
                                (typingIds.has(item.conversationId) || item.otherUserTyping)
                                  ? { color: PRAIRI_SURFACES.trackerComplete }
                                  : undefined
                              }
                            >
                              {isStartup && isItemEnded
                                ? 'Conversation ended'
                                : typingIds.has(item.conversationId) || item.otherUserTyping
                                  ? 'typing...'
                                  : displayPrairiTeamWelcomePreview(
                                      item.lastMessagePreview,
                                      isPrairiTeam,
                                      prairiViewer
                                    ) || 'No preview'}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </InboxScrollColumn>
          </aside>

          <div
            className={`bg-[#1d1d1d] lg:bg-black rounded-xl lg:rounded-none lg:min-h-0 flex-col lg:flex min-w-0 overflow-hidden px-1.5 pt-1 pb-0 max-lg:pt-3 lg:px-0 lg:pt-0 lg:pb-0 lg:pr-3 ${inboxSectionBottom} ${
              mobileThreadOpen
                ? 'flex max-lg:h-[calc(100dvh-3.25rem)] max-lg:rounded-none'
                : 'hidden'
            }`}
          >
            {/* Same height as the Inbox tab so chat + card tops match the list. */}
            <div
              className="pointer-events-none invisible relative hidden flex-shrink-0 justify-center -mb-4 lg:flex"
              aria-hidden
            >
              <div className="w-fit min-w-[9.5rem] px-12 py-2 text-sm leading-snug">Inbox</div>
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
              <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-[#1d1d1d] lg:overflow-hidden lg:rounded-none">
                <section className="flex min-h-0 min-w-0 flex-1 flex-col">
                  {!selectedConversation ? (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                      Select a conversation to view messages.
                    </div>
                  ) : (
                    <>
                      <div className="flex h-0 min-h-0 flex-1 flex-col overflow-hidden">
                        <div className="flex flex-shrink-0 flex-col gap-2 px-3 pt-1 pb-2 max-lg:gap-4 max-lg:pt-4 max-lg:pb-3 lg:p-4">
                          <button
                            type="button"
                            className="lg:hidden flex w-fit items-center gap-1.5 text-sm text-[#c8c8c8]"
                            onClick={() => setMobileThreadOpen(false)}
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                            Inbox
                          </button>
                          <div className="flex items-start gap-3">
                            {(() => {
                              const canOpenStartupProfile =
                                selectedConversation.otherUser.userType === 'startup' &&
                                !isPrairiTeamParticipant(selectedConversation.otherUser) &&
                                !!selectedConversation.startupId &&
                                selectedConversation.startupId !== SYNTHETIC_PRAIRI_TEAM_STARTUP_ID;
                              const openStartupProfile = () => {
                                if (!canOpenStartupProfile) return;
                                const id = selectedConversation.startupId!;
                                const slug = startupNameSlug(
                                  selectedStartupProfile?.companyName ||
                                    selectedConversation.otherUser.companyName ||
                                    selectedConversation.otherUser.name
                                );
                                if (slug && slug !== 'startup') {
                                  navigate(`/dashboard/${slug}`);
                                } else {
                                  navigate(`/dashboard?startupId=${encodeURIComponent(id)}`);
                                }
                              };
                              return (
                                <div className="grid min-w-0 flex-1 grid-cols-[6rem_minmax(0,1fr)] items-start gap-x-3 lg:grid-cols-[calc(5rem*16/9)_minmax(0,1fr)]">
                                  {canOpenStartupProfile ? (
                                    <button
                                      type="button"
                                      onClick={openStartupProfile}
                                      className="flex-shrink-0 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-electric-400"
                                      aria-label={`Open ${conversationDisplayName(selectedConversation.otherUser)} profile`}
                                    >
                                      <ConversationAvatar
                                        user={selectedConversation.otherUser}
                                        size="header"
                                        surfaceClass="bg-[#1d1d1d]"
                                      />
                                    </button>
                                  ) : (
                                    <ConversationAvatar
                                      user={selectedConversation.otherUser}
                                      size="header"
                                      surfaceClass="bg-[#1d1d1d]"
                                    />
                                  )}
                                  <div className="min-w-0 pt-0.5 text-left lg:pt-4">
                                    {canOpenStartupProfile ? (
                                      <button
                                        type="button"
                                        onClick={openStartupProfile}
                                        className={`text-white font-semibold text-left hover:text-electric-400 hover:underline focus:outline-none focus-visible:underline focus-visible:text-electric-400 ${
                                          isPrairiTeamParticipant(selectedConversation.otherUser)
                                            ? 'tracking-[0.32em]'
                                            : ''
                                        }`}
                                      >
                                        {conversationDisplayName(selectedConversation.otherUser)}
                                      </button>
                                    ) : (
                                      <p
                                        className={`text-white font-semibold ${
                                          isPrairiTeamParticipant(selectedConversation.otherUser)
                                            ? 'tracking-[0.32em]'
                                            : ''
                                        }`}
                                      >
                                        {conversationDisplayName(selectedConversation.otherUser)}
                                      </p>
                                    )}
                                    {selectedStartupProfile?.foundersData?.[0]?.name ? (
                                      <p className="text-gray-200 text-base mt-0.5">
                                        {`Founder: ${selectedStartupProfile.foundersData[0].name}`}
                                      </p>
                                    ) : null}
                                    {selectedConversationEnded ? (
                                      <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5 italic">
                                        <span className="w-2 h-2 rounded-full bg-red-500 inline-block flex-shrink-0" />
                                        Conversation ended
                                      </p>
                                    ) : selectedConversation.otherUser.isOnline === true ? (
                                      <p className="text-gray-300 text-xs mt-1.5 flex items-center gap-1.5">
                                        <PresenceDot className="w-2 h-2" />
                                        Online
                                      </p>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })()}
                            <span className="ml-auto text-gray-500 text-lg leading-none tracking-widest">
                              ⋯
                            </span>
                          </div>
                        </div>

                        <input
                          ref={photoInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          className="hidden"
                          onChange={event => void handleOwnPhotoSelected(event)}
                        />
                        <InboxScrollColumn
                          className="h-0 flex-1 overflow-hidden"
                          pinToEnd
                          topFadeSrc={messageThreadFade}
                          thumbHeightPx={inboxThumbPx > 0 ? inboxThumbPx : undefined}
                          thumbGutter
                          contentKey={`${selectedConversation.conversationId}:${messages.length}:${messages[messages.length - 1]?.id || ''}`}
                        >
                          <div className="space-y-5 p-4">
                            {loadingMessages ? (
                              <p className="text-gray-400 text-sm">Loading messages...</p>
                            ) : messageError ? (
                              <div className="space-y-2">
                                <p className="text-red-400 text-sm">{messageError}</p>
                                <button
                                  onClick={() => setMessagesReloadToken(token => token + 1)}
                                  className="px-3 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm"
                                >
                                  Retry
                                </button>
                              </div>
                            ) : messages.length === 0 ? (
                              <p className="text-gray-400 text-sm">No messages yet.</p>
                            ) : (
                              messages.map((message, index) => {
                                const isMine = message.senderId === user?.id;
                                const isPrairiTeam = isPrairiTeamParticipant(
                                  selectedConversation.otherUser
                                );
                                const body = displayPrairiTeamWelcomeBody(
                                  message.body,
                                  isPrairiTeam,
                                  prairiViewer
                                );
                                const dayLabel = formatInboxDay(message.createdAt);
                                const prev = index > 0 ? messages[index - 1] : null;
                                const prevDay = prev ? formatInboxDay(prev.createdAt) : '';
                                const showDay = !!dayLabel && dayLabel !== prevDay;
                                const sameSender = prev?.senderId === message.senderId && !showDay;
                                const sender = isMine
                                  ? {
                                      name: user?.name,
                                      companyName:
                                        user?.userType === 'startup'
                                          ? startupProfile?.companyName
                                          : investorProfile?.companyName,
                                      userType: (user?.userType === 'startup'
                                        ? 'startup'
                                        : 'investor') as MessagingUserType,
                                      profilePictureUrl:
                                        user?.userType === 'startup'
                                          ? startupProfile?.thumbnailUrl
                                          : investorProfile?.profilePictureUrl,
                                    }
                                  : selectedConversation.otherUser;
                                const senderIsPrairi = isPrairiTeamParticipant(sender);
                                const showSeen =
                                  isMine &&
                                  !!message.readAt &&
                                  !messages
                                    .slice(index + 1)
                                    .some(item => item.senderId === user?.id && item.readAt);
                                return (
                                  <div key={message.id}>
                                    {showDay && (
                                      <div className="mb-3 flex items-center gap-3 py-2">
                                        <div
                                          className="h-px flex-1"
                                          style={{ backgroundColor: INBOX_DATE_GREY }}
                                        />
                                        <span
                                          className="whitespace-nowrap text-xs"
                                          style={{ color: INBOX_DATE_GREY }}
                                        >
                                          {dayLabel}
                                        </span>
                                        <div
                                          className="h-px flex-1"
                                          style={{ backgroundColor: INBOX_DATE_GREY }}
                                        />
                                      </div>
                                    )}
                                    <div className="grid w-full grid-cols-[4.5rem_minmax(0,1fr)] items-start gap-x-2 text-left text-xs text-gray-100 lg:text-base lg:grid-cols-[calc(3.5rem*16/9)_minmax(0,1fr)]">
                                      <div>
                                        {sameSender ? null : (
                                          <div className="flex flex-col items-start gap-1">
                                            <ConversationAvatar
                                              user={sender}
                                              size="message"
                                              surfaceClass="bg-[#1d1d1d]"
                                            />
                                            {isMine &&
                                            user?.userType === 'investor' &&
                                            !sender.profilePictureUrl?.trim() ? (
                                              <button
                                                type="button"
                                                onClick={() => photoInputRef.current?.click()}
                                                disabled={photoUploading}
                                                className="max-w-full text-left text-[11px] leading-tight text-emerald-400 underline underline-offset-2 hover:text-emerald-300 disabled:opacity-50"
                                              >
                                                {photoUploading ? 'Uploading…' : 'Upload photo'}
                                              </button>
                                            ) : null}
                                          </div>
                                        )}
                                      </div>
                                      <div className="min-w-0 w-full">
                                        {sameSender ? null : (
                                          <div className="flex h-[2.8125rem] items-center gap-3 lg:h-14">
                                            <p
                                              className={`min-w-0 font-bold text-white ${
                                                senderIsPrairi ? 'tracking-[0.32em]' : ''
                                              }`}
                                            >
                                              {conversationDisplayName(sender)}
                                            </p>
                                            <span
                                              className="ml-auto whitespace-nowrap text-xs"
                                              style={{ color: INBOX_DATE_GREY }}
                                            >
                                              {formatInboxTime(message.createdAt)}
                                            </span>
                                          </div>
                                        )}
                                        <div className={sameSender ? undefined : 'mt-5'}>
                                          <MessageBody
                                            body={body}
                                            attachment={message.attachment}
                                          />
                                        </div>
                                        {showSeen ? (
                                          <div className="mt-1 flex justify-end">
                                            <ConversationAvatar
                                              user={selectedConversation.otherUser}
                                              size="receipt"
                                              surfaceClass="bg-[#1d1d1d]"
                                            />
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </InboxScrollColumn>
                      </div>

                      <div className="mt-auto flex-shrink-0 bg-black pb-2 max-lg:-mx-1.5 lg:mx-0 lg:bg-[#1d1d1d] lg:pb-0">
                        <div className="bg-[#111111] px-0.5 pt-1.5 pb-0 lg:px-1 lg:pt-2">
                          {selectedConversationEnded && isStartup ? (
                            <div className="rounded-xl bg-[#1E1E1E] border border-gray-700/80 overflow-hidden min-h-[180px] flex flex-col">
                              <div className="relative flex-1 min-h-[160px]">
                                <div
                                  className="absolute inset-0 px-3 py-3 text-base text-gray-500/40 pointer-events-none select-none"
                                  aria-hidden
                                >
                                  Write a message...
                                </div>
                                <div className="relative z-[1] flex h-full min-h-[160px] items-center justify-center px-6 py-4">
                                  <div className="text-center text-sm leading-snug text-red-400 max-w-md">
                                    <p>
                                      This investor has decided not to pursue this opportunity
                                      further.
                                    </p>
                                    <p className="mt-4">
                                      <Link
                                        to="/dashboard"
                                        className="underline underline-offset-2 hover:text-red-300"
                                      >
                                        View feedback
                                      </Link>
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="border-t border-gray-700/80">
                                <div className="flex items-center justify-between px-3 pt-2 pb-2">
                                  <span className="text-xs text-gray-500">0/2000</span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      disabled
                                      className="px-3 py-1.5 text-xs font-medium rounded-full text-white opacity-40 cursor-not-allowed"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      disabled
                                      className="px-4 py-1.5 text-xs font-semibold rounded-full bg-emerald-700 text-white opacity-40 cursor-not-allowed"
                                    >
                                      Send
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : selectedConversationEnded ? (
                            <div className="rounded-xl bg-[#1E1E1E] border border-gray-700/80 overflow-hidden min-h-[180px] flex flex-col">
                              <div className="relative flex-1 min-h-[160px]">
                                <div
                                  className="absolute inset-0 px-3 py-3 text-base text-gray-500/40 pointer-events-none select-none"
                                  aria-hidden
                                >
                                  Write a message...
                                </div>
                                <div className="relative z-[1] flex h-full min-h-[160px] items-center justify-center px-6 py-4">
                                  <div className="text-center space-y-1">
                                    <p className="text-sm font-medium text-red-500">
                                      You&apos;ve ended this conversation
                                    </p>
                                    {selectedConversation?.investorFeedbackSummary ? (
                                      <p className="text-sm text-gray-300 mt-2 max-w-md mx-auto">
                                        Your feedback:{' '}
                                        <span className="text-white underline underline-offset-2">
                                          {selectedConversation.investorFeedbackSummary}
                                        </span>
                                      </p>
                                    ) : null}
                                    <p className="text-sm font-medium text-red-500">
                                      To message this startup again,{' '}
                                      <button
                                        type="button"
                                        disabled={readdingFavorite}
                                        onClick={() => {
                                          const startupId = selectedConversation.startupId;
                                          if (
                                            !startupId ||
                                            startupId === SYNTHETIC_PRAIRI_TEAM_STARTUP_ID ||
                                            readdingFavorite
                                          ) {
                                            return;
                                          }
                                          // Optimistic: reopen the thread immediately.
                                          addFavoriteLocally(
                                            startupId,
                                            selectedStartupProfile || undefined
                                          );
                                          updateConversation(selectedConversation.conversationId, {
                                            favoriteActive: true,
                                          });
                                          void addToFavorites(startupId, {
                                            source: 'catalog',
                                          }).then(async result => {
                                            if (!result.success) {
                                              removeFavoriteLocally(startupId);
                                              updateConversation(
                                                selectedConversation.conversationId,
                                                { favoriteActive: false }
                                              );
                                              return;
                                            }
                                            if (
                                              inboxRefollowNeedsContacted(result.investorStatus)
                                            ) {
                                              try {
                                                const updateFn = httpsCallable<
                                                  { startupId: string; newStatus: DealStatus },
                                                  { success: boolean }
                                                >(getFunctions(), 'updateDealStatus');
                                                await updateFn({
                                                  startupId,
                                                  newStatus: DEAL_STATUS.contacted,
                                                });
                                              } catch {
                                                // Follow still succeeds if the tracker write fails.
                                              }
                                            }
                                            invalidateFavorites();
                                            await refetchConversations({ silent: true });
                                            updateConversation(
                                              selectedConversation.conversationId,
                                              { favoriteActive: true }
                                            );
                                          });
                                        }}
                                        className="underline underline-offset-2 hover:text-red-300 disabled:opacity-50"
                                      >
                                        follow them
                                      </button>
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="border-t border-gray-700/80">
                                <div className="flex items-center justify-between px-3 pt-2 pb-2">
                                  <span className="text-xs text-gray-500">0/2000</span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      disabled
                                      className="px-3 py-1.5 text-xs font-medium rounded-full text-white opacity-40 cursor-not-allowed"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      disabled
                                      className="px-4 py-1.5 text-xs font-semibold rounded-full bg-emerald-700 text-white opacity-40 cursor-not-allowed"
                                    >
                                      Send
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-xl bg-[#1E1E1E] border border-gray-700/80 overflow-hidden min-h-0 flex flex-col lg:min-h-[180px]">
                              <textarea
                                ref={composerRef}
                                value={composerText}
                                onChange={event =>
                                  setComposerText(event.target.value.slice(0, 2000))
                                }
                                onKeyDown={event => {
                                  if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault();
                                    handleSend();
                                  }
                                }}
                                rows={3}
                                maxLength={2000}
                                placeholder="Write a message..."
                                className="h-12 w-full min-h-0 flex-1 bg-transparent border-0 px-3 py-2 text-base text-white placeholder:text-gray-500 focus:outline-none focus:ring-0 resize-none lg:h-auto lg:min-h-[160px] lg:py-3"
                                disabled={sending}
                                aria-label="Write a message"
                              />
                              <div className="border-t border-gray-700/80">
                                <div className="flex items-center justify-between gap-2 px-3 pt-2 pb-2">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <MessageAttachmentPicker
                                      fileName={attachmentFile?.name ?? null}
                                      disabled={sending}
                                      error={attachmentError}
                                      onFile={file => {
                                        const error = messageAttachmentTypeError(file);
                                        if (error) {
                                          setAttachmentError(error);
                                          setAttachmentFile(null);
                                          return;
                                        }
                                        setAttachmentError(null);
                                        setAttachmentFile(file);
                                      }}
                                      onClear={() => {
                                        setAttachmentFile(null);
                                        setAttachmentError(null);
                                      }}
                                    />
                                    <span className="flex-shrink-0 text-xs text-gray-500">
                                      {composerText.length}/2000
                                    </span>
                                  </div>
                                  <div className="flex flex-shrink-0 items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setComposerText('');
                                        setAttachmentFile(null);
                                        setAttachmentError(null);
                                      }}
                                      disabled={
                                        sending || (composerText.length === 0 && !attachmentFile)
                                      }
                                      className="px-3 py-1.5 text-xs font-medium rounded-full text-white hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleSend}
                                      disabled={
                                        (!composerText.trim() && !attachmentFile) || sending
                                      }
                                      className="px-4 py-1.5 text-xs font-semibold rounded-full bg-emerald-700 text-white hover:bg-emerald-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      Send
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </section>
              </div>

              {/* Same 12px black gutter as dashboard / Settings between chat and profile. */}
              {(showStartupProfilePanel || showInvestorProfilePanel) && (
                <div
                  className="hidden w-3 shrink-0 self-stretch lg:block"
                  style={{ backgroundColor: PRAIRI_SURFACES.divider }}
                  aria-hidden
                />
              )}

              {showStartupProfilePanel && (
                <div
                  className="relative hidden min-h-0 min-w-0 flex-shrink-0 flex-col overflow-visible lg:flex lg:h-full lg:max-h-full lg:w-[46%] lg:pl-1.5 lg:pr-3 lg:pb-0 lg:pt-0"
                  style={{ backgroundColor: PRAIRI_SURFACES.page }}
                >
                  <aside
                    ref={profileCardRef}
                    className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-visible rounded-3xl border-[1.5px] border-black bg-[#303030]"
                  >
                    <div className="flex-1 min-h-0 flex flex-col overflow-visible">
                      <StartupProfileComponent
                        key={selectedConversation?.startupId || selectedConversationId || 'startup'}
                        profile={selectedStartupProfile}
                        compactDeck
                        pitchDeckOnDemand
                        messagesPane
                        contactAlreadyOpen
                        isFavorited={
                          !!selectedConversation?.startupId &&
                          selectedConversation.startupId !== SYNTHETIC_PRAIRI_TEAM_STARTUP_ID &&
                          !selectedConversationEnded &&
                          (favoritesReady
                            ? favoriteIds.has(selectedConversation.startupId)
                            : selectedConversation.favoriteActive === true)
                        }
                        compactBgClass="bg-[#303030]"
                        isOnline={selectedConversation?.otherUser.isOnline === true}
                        onDismissFromFeedRequested={
                          selectedConversation?.startupId &&
                          selectedConversation.startupId !== SYNTHETIC_PRAIRI_TEAM_STARTUP_ID
                            ? handleInboxDismissFromFeed
                            : undefined
                        }
                        onFavoriteChange={(startupId, nextFavorited) => {
                          const conversationId = selectedConversation?.conversationId;
                          if (selectedConversation?.startupId === startupId && conversationId) {
                            updateConversation(conversationId, {
                              favoriteActive: nextFavorited,
                            });
                          }
                          if (nextFavorited) {
                            // Optimistic only. Do not invalidate/refetch here — a getFavorites
                            // before addToFavorites finishes wipes the stub and snaps Follow back.
                            // useFavorites.addToFavorites invalidates after the server write.
                            addFavoriteLocally(startupId, selectedStartupProfile || undefined);
                            return;
                          }
                          // Unfollow: keep the local ended state. A refetch here still sees
                          // the favorite on the server and snaps the thread back to followed.
                          removeFavoriteLocally(startupId);
                        }}
                        onOpenFullProfile={() => {
                          const profile = selectedStartupProfile;
                          const id = profile?.id;
                          if (!id || id === SYNTHETIC_PRAIRI_TEAM_STARTUP_ID) return;
                          const slug = startupNameSlug(profile?.companyName);
                          if (slug && slug !== 'startup') {
                            navigate(`/dashboard/${slug}`);
                          } else {
                            navigate(`/dashboard?startupId=${encodeURIComponent(id)}`);
                          }
                        }}
                      />
                    </div>
                  </aside>
                </div>
              )}

              {showInvestorProfilePanel && (
                <div
                  className="relative hidden min-h-0 min-w-0 flex-shrink-0 flex-col lg:flex lg:h-full lg:max-h-full lg:w-[38.8%] lg:pl-1.5 lg:pr-3 lg:pb-0 lg:pt-0"
                  style={{ backgroundColor: PRAIRI_SURFACES.page }}
                >
                  <aside
                    ref={profileCardRef}
                    className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-visible rounded-3xl border-[1.5px] border-black bg-[#333333]"
                  >
                    {!selectedConversation ? (
                      <div className="h-full flex items-center justify-center text-gray-400 text-sm p-4 text-center">
                        Select a conversation to view the investor profile.
                      </div>
                    ) : (
                      <div className="relative flex h-full min-h-0 flex-col overflow-visible">
                        <InboxScrollColumn
                          className="flex-1"
                          thumbOutside
                          thumbHeightPx={inboxThumbPx > 0 ? inboxThumbPx : undefined}
                          contentKey={
                            selectedInvestorProfile?.id || selectedConversationId || 'empty'
                          }
                        >
                          {selectedInvestorProfile ? (
                            <div className="px-5 pb-3 pt-9 text-left sm:px-7">
                              <InvestorProfileComponent
                                profile={selectedInvestorProfile}
                                startupDashboard
                                chatAvatarClass="w-14 h-14"
                                inboxChat
                                isOwnProfile={
                                  user?.userType === 'investor' &&
                                  user.id === selectedInvestorProfile.userId
                                }
                                investorName={selectedConversation.otherUser.name}
                                isOnline={
                                  selectedConversationEnded
                                    ? false
                                    : selectedConversation.otherUser.isOnline === true
                                }
                                presenceEnded={selectedConversationEnded}
                                onContactClick={() => composerRef.current?.focus()}
                              />
                            </div>
                          ) : (
                            <p className="p-6 text-sm text-gray-400">Loading profile...</p>
                          )}
                        </InboxScrollColumn>
                        {isStartup &&
                        selectedConversation.otherUser.userType === 'investor' &&
                        !isPrairiTeamParticipant(selectedConversation.otherUser) ? (
                          <div className="relative shrink-0 overflow-hidden rounded-b-3xl bg-[#333333]">
                            <img
                              src={profileCardFade}
                              alt=""
                              aria-hidden
                              className="pointer-events-none absolute inset-x-0 bottom-full z-20 h-20 w-full object-fill lg:h-[189px]"
                            />
                            <div className="px-4 py-4 sm:px-5">
                              <DealTracker
                                userType={USER_TYPES.startup}
                                status={startupInboxDealStatus}
                                primaryActionLabel={ACTION_LABELS.startup[startupInboxDealStatus]}
                                onPrimaryActionClick={advanceStartupInboxDeal}
                                onCompletedStepClick={rewindStartupInboxDeal}
                                primaryActionLoading={inboxDealUpdating}
                                primaryActionDisabled={inboxDealLoading || inboxDealUpdating}
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </aside>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isInvestor && (
        <div className="w-full bg-[#1d1d1d] pt-20 pb-10">
          <div className="relative z-10 w-full overflow-visible pl-[2.5vw]">
            <StartupRow
              title={CATALOG_CATEGORY_LABELS.yourFavorites}
              startups={favorites}
              onSelectStartup={startup => {
                const slug = startupNameSlug(startup.companyName);
                if (slug && slug !== 'startup') {
                  navigate(`/dashboard/${slug}`);
                  return;
                }
                const id = resolveStartupId(startup);
                if (id) navigate(`/dashboard?startupId=${encodeURIComponent(id)}`);
              }}
              favoriteIds={favoriteIds}
              hidePreviouslyViewedBadge
              favoriteBadgeIconOnly
              categoryBox
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InboxPage;
